/* eslint-disable no-case-declarations */
import { combineReducers } from 'redux';
import {
  FETCH_ASSIGNMENTS_STARTED,
  FETCH_ASSIGNMENTS_SUCCESS,
  FETCH_ASSIGNMENTS_FAILED,
  FETCH_ASSIGNMENT_STARTED,
  FETCH_ASSIGNMENT_SUCCESS,
  FETCH_ASSIGNMENT_FAILED,
  FETCH_GRADES_STARTED,
  FETCH_GRADES_SUCCESS,
  FETCH_GRADES_FAILED,
  FETCH_STUDENT_GRADES_STARTED,
  FETCH_STUDENT_GRADES_SUCCESS,
  FETCH_STUDENT_GRADES_FAILED,
  CREATE_ASSIGNMENT_STARTED,
  CREATE_ASSIGNMENT_SUCCESS,
  CREATE_ASSIGNMENT_FAILED,
  RELEASE_GRADES_STARTED,
  RELEASE_GRADES_SUCCESS,
  RELEASE_GRADES_FAILED,
  GRADE_MANUAL_ASSIGNMENT_STARTED,
  GRADE_MANUAL_ASSIGNMENT_SUCCESS,
  GRADE_MANUAL_ASSIGNMENT_FAILED,
  UPDATE_ASSIGNMENT_STARTED,
  UPDATE_ASSIGNMENT_SUCCESS,
  UPDATE_ASSIGNMENT_FAILED,
  DELETE_ASSIGNMENT_SUCCESS,
  UPDATE_ASSIGNMENT_GRADES_SUCCESS,
  IMPORT_MANUAL_ASSIGNMENT_GRADES_STARTED,
  IMPORT_MANUAL_ASSIGNMENT_GRADES_SUCCESS,
  IMPORT_MANUAL_ASSIGNMENT_GRADES_FAILED,
} from './assignments.types';

import { CHANGE_CURRENT_SPACE } from '$data/core/core.types';

import normalizeGrades from './utils/normalizeGrades';
import findAndUpdateGrade from './utils/findAndUpdateGrade';
import updateGrades from './utils/updateGrades';

const statusReducer = (
  state = { isLoading: false, error: null },
  { type, error }
) => {
  switch (type) {
    case FETCH_ASSIGNMENTS_STARTED:
      return {
        isLoading: true,
        error: null,
      };
    case FETCH_ASSIGNMENTS_SUCCESS:
      return {
        isLoading: false,
        error: null,
      };
    case FETCH_ASSIGNMENTS_FAILED:
      return {
        isLoading: false,
        error,
      };
    default:
      return state;
  }
};

const assignmentStatusReducer = (
  state = { isLoading: false, error: null },
  { type, error }
) => {
  switch (type) {
    case FETCH_ASSIGNMENT_STARTED:
      return {
        isLoading: true,
        error: null,
      };
    case FETCH_ASSIGNMENT_SUCCESS:
      return {
        isLoading: false,
        error: null,
      };
    case FETCH_ASSIGNMENT_FAILED:
      return {
        isLoading: false,
        error,
      };
    default:
      return state;
  }
};

const gradesStatusReducer = (
  state = { isLoading: false, error: null },
  { type, error }
) => {
  switch (type) {
    case FETCH_GRADES_STARTED:
      return {
        isLoading: true,
        error: null,
      };
    case FETCH_GRADES_SUCCESS:
      return {
        isLoading: false,
        error: null,
      };
    case FETCH_GRADES_FAILED:
      return {
        isLoading: false,
        error,
      };
    default:
      return state;
  }
};

const studentGradesStatusReducer = (
  state = { isLoading: false, error: null },
  { type, error }
) => {
  switch (type) {
    case FETCH_STUDENT_GRADES_STARTED:
      return {
        isLoading: true,
        error: null,
      };
    case FETCH_STUDENT_GRADES_SUCCESS:
      return {
        isLoading: false,
        error: null,
      };
    case FETCH_STUDENT_GRADES_FAILED:
      return {
        isLoading: false,
        error,
      };
    default:
      return state;
  }
};

const createAssignmentStatusReducer = (
  state = { isCreating: false, error: null },
  { type, error }
) => {
  switch (type) {
    case CREATE_ASSIGNMENT_STARTED:
      return {
        isCreating: true,
        error: null,
      };
    case CREATE_ASSIGNMENT_SUCCESS:
      return {
        isCreating: false,
        error: null,
      };
    case CREATE_ASSIGNMENT_FAILED:
      return {
        isCreating: false,
        error,
      };
    default:
      return state;
  }
};

const updateAssignmentStatusReducer = (
  state = { isUpdating: false, error: null },
  { type, error }
) => {
  switch (type) {
    case UPDATE_ASSIGNMENT_STARTED:
      return {
        isUpdating: true,
        error: null,
      };
    case UPDATE_ASSIGNMENT_SUCCESS:
      return {
        isUpdating: false,
        error: null,
      };
    case UPDATE_ASSIGNMENT_FAILED:
      return {
        isUpdating: false,
        error,
      };
    default:
      return state;
  }
};

const releaseGradesStatusReducer = (
  state = { isLoading: false, error: null },
  { type, error }
) => {
  switch (type) {
    case RELEASE_GRADES_STARTED:
      return {
        isLoading: true,
        error: null,
      };
    case RELEASE_GRADES_SUCCESS:
      return {
        isLoading: false,
        error: null,
      };
    case RELEASE_GRADES_FAILED:
      return {
        isLoading: false,
        error,
      };
    default:
      return state;
  }
};

const gradesReducer = (
  state = {},
  { type, grades, assignmentId, grade, studentId }
) => {
  if (
    type === FETCH_GRADES_SUCCESS ||
    type === UPDATE_ASSIGNMENT_GRADES_SUCCESS
  ) {
    return {
      ...state,
      ...grades,
    };
  }

  if (type === GRADE_MANUAL_ASSIGNMENT_SUCCESS) {
    return {
      ...state,
      [assignmentId]: findAndUpdateGrade({
        grades: state[assignmentId],
        studentId,
        grade,
      }),
    };
  }

  if (type === IMPORT_MANUAL_ASSIGNMENT_GRADES_SUCCESS) {
    return {
      ...state,
      [assignmentId]: updateGrades({
        currentGrades: state[assignmentId],
        updatedGrades: grades,
      }),
    };
  }

  // reset on changing space
  if (type === CHANGE_CURRENT_SPACE) {
    return {};
  }

  return state;
};

const studentGradesReducer = (state = {}, { type, grades, studentId }) => {
  if (type === FETCH_STUDENT_GRADES_SUCCESS) {
    return {
      ...state,
      [studentId]: normalizeGrades(grades),
    };
  }

  // reset on changing space
  if (type === CHANGE_CURRENT_SPACE) {
    return {};
  }

  return state;
};

const byIdReducer = (
  state = {},
  {
    type,
    assignments,
    assignment,
    createdAssignment,
    assignmentId: assignmentWithGradesReleasedId,
    gradesReleasedAt,
    updatedAssignment,
    assignmentId: deletedAssignmentId,
  }
) => {
  if (type === FETCH_ASSIGNMENTS_SUCCESS) {
    return assignments.reduce(
      (accumulatedItems, item) => ({
        ...accumulatedItems,
        [item.id]: item,
      }),
      {}
    );
  }

  // single assignment fetched
  if (type === FETCH_ASSIGNMENT_SUCCESS) {
    return {
      ...state,
      [assignment.id]: {
        ...assignment,
      },
    };
  }

  if (type === CREATE_ASSIGNMENT_SUCCESS) {
    return {
      ...state,
      [createdAssignment.id]: {
        ...createdAssignment,
      },
    };
  }

  if (type === RELEASE_GRADES_SUCCESS) {
    return {
      ...state,
      [assignmentWithGradesReleasedId]: {
        ...state[assignmentWithGradesReleasedId],
        gradesReleasedAt,
      },
    };
  }

  if (type === UPDATE_ASSIGNMENT_SUCCESS) {
    return {
      ...state,
      [updatedAssignment.id]: {
        ...updatedAssignment,
      },
    };
  }

  if (type === DELETE_ASSIGNMENT_SUCCESS) {
    const { [deletedAssignmentId]: _, ...rest } = state;

    return rest;
  }

  // reset on changing space
  if (type === CHANGE_CURRENT_SPACE) {
    return {};
  }

  return state;
};

const idsReducer = (
  state = [],
  {
    type,
    assignments,
    assignment,
    createdAssignment,
    assignmentId: deletedAssignmentId,
  }
) => {
  if (type === FETCH_ASSIGNMENTS_SUCCESS) {
    return assignments.map((item) => item.id);
  }

  // single assignment fetched
  if (type === FETCH_ASSIGNMENT_SUCCESS) {
    const existingRecord = state.find(
      (assignmentId) => assignmentId === assignment.id
    );

    if (!existingRecord) {
      return [...state, assignment.id];
    }
  }

  if (type === CREATE_ASSIGNMENT_SUCCESS) {
    const existingRecord = state.find(
      (assignmentId) => assignmentId === createdAssignment.id
    );

    if (!existingRecord) {
      return [createdAssignment.id, ...state];
    }
  }

  if (type === DELETE_ASSIGNMENT_SUCCESS) {
    return state.filter((assignmentId) => assignmentId !== deletedAssignmentId);
  }

  // reset on changing space
  if (type === CHANGE_CURRENT_SPACE) {
    return [];
  }

  return state;
};

const gradeManualAssignmentStatusReducer = (
  state = {},
  { type, error, assignmentId, studentId }
) => {
  switch (type) {
    case GRADE_MANUAL_ASSIGNMENT_STARTED:
      return {
        ...state,
        [assignmentId]: {
          ...state[assignmentId],
          [studentId]: {
            isLoading: true,
            error: null,
            savedAt: null,
          },
        },
      };
    case GRADE_MANUAL_ASSIGNMENT_SUCCESS:
      return {
        ...state,
        [assignmentId]: {
          ...state[assignmentId],
          [studentId]: {
            isLoading: false,
            error: null,
            savedAt: new Date(),
          },
        },
      };
    case GRADE_MANUAL_ASSIGNMENT_FAILED:
      return {
        ...state,
        [assignmentId]: {
          ...state[assignmentId],
          [studentId]: {
            isLoading: false,
            error,
            savedAt: null,
          },
        },
      };
    default:
      return state;
  }
};

const importManualAssignmentGradesReducer = (
  state = {},
  { type, error, assignmentId }
) => {
  switch (type) {
    case IMPORT_MANUAL_ASSIGNMENT_GRADES_STARTED:
      return {
        ...state,
        [assignmentId]: {
          isLoading: true,
          error: null,
        },
      };
    case IMPORT_MANUAL_ASSIGNMENT_GRADES_SUCCESS:
      return {
        ...state,
        [assignmentId]: {
          isLoading: false,
          error: null,
        },
      };
    case IMPORT_MANUAL_ASSIGNMENT_GRADES_FAILED:
      return {
        ...state,
        [assignmentId]: {
          isLoading: false,
          error,
        },
      };
    default:
      return state;
  }
};

export default combineReducers({
  status: statusReducer,
  byId: byIdReducer,
  ids: idsReducer,
  assignmentStatus: assignmentStatusReducer,
  grades: gradesReducer,
  gradesStatus: gradesStatusReducer,
  studentGrades: studentGradesReducer,
  studentGradesStatus: studentGradesStatusReducer,
  releaseGradesStatus: releaseGradesStatusReducer,
  createAssignmentStatus: createAssignmentStatusReducer,
  updateAssignmentStatus: updateAssignmentStatusReducer,
  gradeManualAssignmentStatus: gradeManualAssignmentStatusReducer,
  importManualAssignmentGradesStatus: importManualAssignmentGradesReducer,
});
